// Slope bands shared by the map track (slope toggle), its legend and the elevation chart.
export const SLOPE_BANDS = [
    {
        min: 0,
        color: '#4CAF50',
        label: '0–5%'
    },
    {
        min: 5,
        color: '#FFC107',
        label: '5–10%'
    },
    {
        min: 10,
        color: '#FF9800',
        label: '10–15%'
    },
    {
        min: 15,
        color: '#FF5722',
        label: '15–20%'
    },
    {
        min: 20,
        color: '#F44336',
        label: '20%+'
    }
];

// Chart distances are rounded to 10 m: a point-to-point grade is mostly rounding noise. Fixed, not a
// share of the track: the grade of a climb must not depend on how long the outing is.
const GRADE_WINDOW_METERS = 100;
// A band shorter than 1/150 of the track (never under 100 m) is a sliver on the chart.
const MIN_ZONE_METERS = 100;
const ZONES_PER_TRACK = 150;
const EPSILON = 1e-9;

export const slopeBandIndex = (grade) => {
    const absoluteGrade = Math.abs(grade);

    for (let i = SLOPE_BANDS.length - 1; i > 0; i--) {
        if (absoluteGrade >= SLOPE_BANDS[i].min) {
            return i;
        }
    }

    return 0;
};

const _hexToRgba = (hex, alpha) => {
    const [r, g, b] = [1, 3, 5].map((offset) => parseInt(hex.slice(offset, offset + 2), 16));

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const minZoneMeters = (points) => {
    const distances = points.map((point) => point?.distance).filter(Number.isFinite);
    const trackMeters = distances.length ? (distances.at(-1) - distances[0]) * 1000 : 0;

    return Math.max(MIN_ZONE_METERS, Math.round(trackMeters / ZONES_PER_TRACK));
};

// Signed grade (%) at each point, over a distance window centred on it. `points`: {distance (km), elevation (m)}.
export const computeGrades = (points, windowMeters = GRADE_WINDOW_METERS) => {
    const grades = points.map(() => null);
    const valid = [];
    points.forEach((point, index) => {
        if (Number.isFinite(point?.distance) && Number.isFinite(point?.elevation)) {
            valid.push(index);
        }
    });

    const halfWindowKm = windowMeters / 2000;
    let low = 0;
    let high = 0;

    valid.forEach((pointIndex, position) => {
        const center = points[pointIndex].distance;

        while (points[valid[low]].distance < center - halfWindowKm - EPSILON) {
            low++;
        }
        high = Math.max(high, position);
        while (high + 1 < valid.length && points[valid[high + 1]].distance <= center + halfWindowKm + EPSILON) {
            high++;
        }

        // Points sparser than the window: fall back to the neighbours.
        const start = low === high ? Math.max(0, low - 1) : low;
        const end = low === high ? Math.min(valid.length - 1, high + 1) : high;
        const first = points[valid[start]];
        const last = points[valid[end]];
        const spanMeters = (last.distance - first.distance) * 1000;

        if (spanMeters > 0) {
            grades[pointIndex] = (last.elevation - first.elevation) / spanMeters * 100;
        }
    });

    return grades;
};

// Highcharts zones (zoneAxis 'x') colouring the profile by absolute grade; empty when no grade is known.
// Consecutive bands shorter than `minZoneMeters` are grouped into chunks of that length, each taking its
// dominant band; a chunk left shorter takes the colour of the zone before it.
export const buildSlopeZones = (points, grades, minZoneMeters = 0) => {
    const segments = [];

    points.forEach((point, index) => {
        if (!Number.isFinite(grades[index])) {
            return;
        }

        const band = slopeBandIndex(grades[index]);
        const last = segments.at(-1);
        if (last) {
            last.end = point.distance;
        }
        if (last?.band !== band) {
            segments.push({
                band,
                start: point.distance,
                end: point.distance
            });
        }
    });

    const lengthMeters = (segment) => (segment.end - segment.start) * 1000;
    const merged = [];
    let run = null;

    const append = (segment) => {
        const previous = merged.at(-1);

        if (previous && previous.band === segment.band) {
            previous.end = segment.end;
        } else {
            merged.push(segment);
        }
    };

    const flushRun = () => {
        if (!run) {
            return;
        }

        const band = run.lengths.indexOf(Math.max(...run.lengths));
        const chunk = {
            band,
            start: run.start,
            end: run.end
        };
        run = null;

        if (merged.length && lengthMeters(chunk) < minZoneMeters) {
            merged.at(-1).end = chunk.end;
        } else {
            append(chunk);
        }
    };

    segments.forEach((segment) => {
        if (lengthMeters(segment) >= minZoneMeters) {
            flushRun();
            append({...segment});
            return;
        }

        run ??= {
            start: segment.start,
            end: segment.start,
            lengths: SLOPE_BANDS.map(() => 0)
        };
        run.end = segment.end;
        run.lengths[segment.band] += lengthMeters(segment);

        if (lengthMeters(run) >= minZoneMeters) {
            flushRun();
        }
    });

    flushRun();

    return merged.map((segment, index) => ({
        ...(index < merged.length - 1 ? {value: segment.end} : {}),
        color: SLOPE_BANDS[segment.band].color,
        fillColor: _hexToRgba(SLOPE_BANDS[segment.band].color, 0.5)
    }));
};
